'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import DemoCredentials from './DemoCredentials';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

const DEMO_ACCOUNTS = [
  { role: 'Solo Artist', email: 'nova@tracklab.io', password: 'StudioNova2026!' },
  { role: 'Band Member', email: 'kai@voidpulse.band', password: 'VoidPulse2026!' },
];

interface Props {
  onSwitchToSignup: () => void;
}

export default function LoginForm({ onSwitchToSignup }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<LoginFormData>({ defaultValues: { remember: false } });

  const onSubmit = async (data: LoginFormData) => {
    setLoading(true);
    // Backend integration: POST /api/auth/login with { email, password, remember }
    await new Promise((r) => setTimeout(r, 1200));
    const valid = DEMO_ACCOUNTS.find(
      (a) => a.email === data.email && a.password === data.password
    );
    if (!valid) {
      setLoading(false);
      setError('root', {
        message: 'Invalid credentials — use the demo accounts below to sign in',
      });
      return;
    }
    toast.success(`Welcome back, ${valid.role}!`);
    router.push('/projects-dashboard');
  };

  const handleGoogle = async () => {
    // Backend integration: initiate Google OAuth flow
    toast.info('Redirecting to Google…');
    await new Promise((r) => setTimeout(r, 800));
    router.push('/projects-dashboard');
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-1">Welcome back</h2>
      <p className="text-sm text-gray-400 mb-8">Sign in to your TrackLab workspace</p>

      {/* Google button */}
      <button
        onClick={handleGoogle}
        className="w-full flex items-center justify-center gap-3 bg-dark-card border border-dark-border rounded-xl py-3 text-sm font-medium text-gray-200 hover:border-brand-violet/40 hover:bg-dark-elevated transition-all duration-200 mb-6 active:scale-[0.98]"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
          <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
          <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
          <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
        </svg>
        Continue with Google
      </button>

      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1 h-px bg-dark-border" />
        <span className="text-xs text-gray-600">or</span>
        <div className="flex-1 h-px bg-dark-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Root error */}
        {errors.root && (
          <div className="bg-red-950/50 border border-red-800/50 rounded-lg px-4 py-3">
            <p className="text-sm text-red-400">{errors.root.message}</p>
          </div>
        )}

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">
            Email address
          </label>
          <input
            type="email"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
            })}
            placeholder="nova@tracklab.io"
            className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand-violet/60 focus:ring-1 focus:ring-brand-violet/30 transition-all duration-200"
          />
          {errors.email && (
            <p className="text-xs text-red-400 mt-1.5">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-medium text-gray-300">Password</label>
            <button type="button" className="text-xs text-brand-violet hover:text-brand-violet-light transition-colors">
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              {...register('password', { required: 'Password is required' })}
              placeholder="••••••••"
              className="w-full bg-dark-card border border-dark-border rounded-xl px-4 py-3 pr-11 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand-violet/60 focus:ring-1 focus:ring-brand-violet/30 transition-all duration-200"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="text-xs text-red-400 mt-1.5">{errors.password.message}</p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="remember"
            {...register('remember')}
            className="w-4 h-4 rounded border-dark-border bg-dark-card accent-brand-violet cursor-pointer"
          />
          <label htmlFor="remember" className="text-sm text-gray-400 cursor-pointer select-none">
            Remember me for 30 days
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-brand-violet hover:bg-brand-violet-dark text-white font-semibold py-3 rounded-xl transition-all duration-200 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-neon-violet"
          style={{ minHeight: '48px' }}
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              <span>Signing in…</span>
            </>
          ) : (
            'Sign In'
          )}
        </button>
      </form>

      <p className="text-sm text-gray-500 text-center mt-6">
        Don&apos;t have an account?{' '}
        <button onClick={onSwitchToSignup} className="text-brand-violet hover:text-brand-violet-light transition-colors font-medium">
          Create one free
        </button>
      </p>

      {/* Demo credentials */}
      <DemoCredentials accounts={DEMO_ACCOUNTS} onSelect={(email, password) => {
        setValue('email', email);
        setValue('password', password);
      }} />
    </div>
  );
}